import { type Mutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type { HouseholdsController } from "../../../api/src/households/households.controller";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { Note } from "../../../api/src/notes/notes.service";
import { deserializeDate, useAuthenticatedMutationNew } from "./api";
import { useAuthenticatedFetchAllPages } from "./pagination";

/**
 * The path of `getHouseholdNotes` for one household. Also the FIRST element of
 * every react-query key the page walk in `useQueryHouseholdNotes` uses, so
 * `[householdNotesPath(id)]` reaches every page of that walk.
 */
export const householdNotesPath = (householdId: number) =>
  `/v1/households/${householdId}/notes`;

/** One item of `GET /households/:id/notes`, as the route serialises it. */
type SerializedHouseholdNote = SerializedObject<
  UnpackResponse<HouseholdsController["getHouseholdNotes"]>
>["data"][number];

/**
 * True for a create, update or delete under `/households`: every mutation key
 * whose method is `POST`, `PUT` or `DELETE` and whose first path element is
 * `"households"`, `useCreateHouseholdNote` among them. The same test as
 * `isHouseholdWrite` in `clients/household/lib.ts`.
 */
const isHouseholdWrite = (mutation: Mutation<unknown, unknown, unknown>) => {
  const [method, root] = mutation.options.mutationKey ?? [];
  return (
    ["POST", "PUT", "DELETE"].includes(String(method)) && root === "households"
  );
};

/**
 * Every note on the household's linked CRM records. `GET /households/:id/notes`
 * answers a page (APP-1653), and its readers (`HouseholdHeader.tsx`,
 * `HouseholdNotesButton.tsx`, `RebalanceHeader.tsx`, `AccountHeader.tsx`) show
 * every note, so this walks every page (`useAuthenticatedFetchAllPages`) and
 * holds `data` back until the last one is in. Each note goes through
 * `deserializeNote`, as before.
 *
 * As `useQueryHouseholdAccounts` in `clients/household/lib.ts` does: when a
 * write under `/households` succeeds (a note created with
 * `useCreateHouseholdNote` among them), the cached pages are dropped and the
 * walk starts again; when the last reader unmounts, the cached pages are
 * dropped; and when `householdId` changes while the reader stays mounted, the
 * walk starts again and `data` stays `undefined` until it is in.
 *
 * `enabled: false` asks for nothing; `data` stays `undefined` and `isLoading`
 * and `isFetching` `false`.
 */
export function useQueryHouseholdNotes(
  householdId: number,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const path = householdNotesPath(householdId);

  // The household whose notes the walk below holds. It trails `householdId`
  // by one render when it changes; the walk is held and `data` withheld
  // meanwhile.
  const [walkedId, setWalkedId] = useState(householdId);
  const isWalkOfId = walkedId === householdId;

  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedHouseholdNote>(
      path,
      {},
      { enabled: enabled && isWalkOfId },
    );

  useEffect(() => {
    if (!isWalkOfId) {
      reload();
      setWalkedId(householdId);
    }
  }, [householdId, isWalkOfId, reload]);

  // Restart the walk when a household write succeeds. Subscribed to the
  // mutation cache's events, as in `useQueryHouseholdAccounts`.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          isHouseholdWrite(event.mutation)
        ) {
          queryClient.removeQueries({ queryKey: [path] });
          reload();
        }
      }),
    [queryClient, path, reload],
  );

  // On unmount, or when the household changes, drop the pages of the path no
  // other mounted reader still observes. This cleanup runs after the walk's
  // own observer has unsubscribed, because the walk's hooks are called above
  // it.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [path],
        predicate: (query) => query.getObserversCount() === 0,
      });
    },
    [queryClient, path],
  );

  const data: Note[] | undefined = useMemo(
    () => (hasEveryRow && isWalkOfId ? rows.map(deserializeNote) : undefined),
    [rows, hasEveryRow, isWalkOfId],
  );

  const isPending = data === undefined && !isError;
  const isLoading = isPending && enabled;

  return {
    isPending,
    isLoading,
    // Every page is read before `data` is set, so the walk is fetching for as
    // long as it is loading.
    isFetching: isLoading,
    isError,
    data,
  };
}

/**
 * `POST /households/:id/notes`. Its success restarts the walk of every mounted
 * `useQueryHouseholdNotes` (a write under `/households`), so the new note is
 * read back with the rest.
 */
export function useCreateHouseholdNote(householdId: number) {
  return useAuthenticatedMutationNew<
    UnpackResponse<HouseholdsController["createHouseholdNote"]>,
    Note | null
  >({
    // The key keeps `"households"` first, so `isHouseholdWrite` matches it;
    // the url is under `/v1` (APP-1653).
    mutationKey: ["POST", "households", householdId, "notes"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: (note) =>
      note.data === null ? null : deserializeNote(note.data),
  });
}

export function deserializeNote(note: SerializedObject<Note>): Note {
  return {
    ...note,
    createdAt: deserializeDate(note.createdAt),
    updatedAt: deserializeDate(note.updatedAt),
  };
}
